// frontend/src/components/Controls.js
import React, { useRef, useState, useEffect } from 'react';
import { getAssetUrl } from '../api/config';

/**
 * Controls component for audio playback, recording indication, and validation.
 * Manages audio playback, visual microphone state, and triggers recording start/stop
 * based on audio events.
 *
 * @param {object} props - Component props.
 * @param {object} props.colors - Object containing color codes for buttons (e.g., { repeat: '#...', record: '#...', check: '#...' }).
 * @param {function} props.onStartRecording - Callback function to explicitly start recording (from useRecorder).
 * @param {function} props.onStopRecording - Callback function to explicitly stop recording (from useRecorder).
 * @param {function} props.onValidate - Callback function to trigger validation/next exercise.
 * @param {boolean} props.recording - Current recording status, passed from the parent (e.g., from useRecorder hook).
 * @param {string} props.context - The current audio context/level (e.g., '1', '2', 'syllables').
 * @param {string} [props.recordingMode='playback'] - Defines when recording starts/stops.
 * - 'playback': Recording starts with audio playback, stops when audio ends (default for Syllables).
 * - 'post-playback': Recording starts when audio ends, stops on validate click (for other exercises).
 */
export default function Controls({ colors, onStartRecording, onStopRecording, onValidate, onPlay, recording, context, recordingMode = 'playback' }) {
    const [hasPlayed, setHasPlayed] = useState(false);
    const [audioEnded, setAudioEnded] = useState(false);
    // New state to control the graphical mic active state specifically for Syllables toggling
    const [syllablesToggleMicActive, setSyllablesToggleMicActive] = useState(false);
    const audioRef = useRef(null); // Ref to access the HTML <audio> element
    const timeoutsRef = useRef([]); // Ref to store setTimeout IDs for cleanup
    const endedHandlerRef = useRef(null); // Ref to the current 'ended' listener, so a replay can detach the previous one

    // Retrieve exerciseId from localStorage for dynamic audio source
    const exerciseId = localStorage.getItem('exerciseId')
    const language = localStorage.getItem('selectedLanguage') || 'it'; // Default to Italian if not set
    const audioSrc = getAssetUrl(`/audios/${exerciseId}/${language}/${context}.mp3`);

    // This schedule is for graphical toggling in the 'syllables' exercise.
    // It does NOT affect the actual recording start/stop.
    const toggleSchedule = {
        'provaSRT': [17000, 28000, 46000, 57000],
        '1': [14000, 27000, 43000, 54000],
        '2': [13000, 24000, 38000, 49000],
        '3': [15000, 26000, 43000, 53000],
        '4': [15000, 26000, 40000, 51000],
        '5': [15000, 27000, 43000, 54000],
        '6': [15000, 27000, 43000, 55000],
        '7': [21000, 89000],
        default: [15000, 27000, 43000, 54000],
    };

    /**
     * useEffect hook to reset component state when the audio context changes.
     * Also cleans up any active timeouts to prevent memory leaks.
     */
    useEffect(() => {
        setHasPlayed(false);
        setAudioEnded(false);
        setSyllablesToggleMicActive(false); // Reset graphical mic toggle state on context change

        // Cleanup function: clears all scheduled timeouts
        return () => {
            timeoutsRef.current.forEach(clearTimeout);
            timeoutsRef.current = []; // Clear the array of timeout IDs
        };
    }, [context]); // Rerun effect when 'context' prop changes

    /**
     * Handles the play button click event.
     * Starts audio playback, initiates recording based on mode, and sets up event listeners
     * for when the audio ends. It also manages the graphical mic toggle for 'syllables' exercise.
     */
    const handlePlay = () => {
        const audio = audioRef.current;
        if (!audio) return; // Exit if audio element is not available

        // Stacca l'eventuale listener 'ended' del play precedente: con un replay
        // (currentTime=0 + play) il vecchio 'ended' non scatta mai e resterebbe
        // appeso, facendo partire più handler quando l'audio finisce davvero.
        if (endedHandlerRef.current) {
            audio.removeEventListener('ended', endedHandlerRef.current);
            endedHandlerRef.current = null;
        }

        // Replay DURANTE una registrazione (modalità 'playback'): chiudiamo la
        // take corrente — onStopRecording la finalizza e la salva come NUOVA
        // versione — così la nuova riproduzione produce una registrazione
        // separata, sincronizzata con l'audio dall'inizio (anziché continuare
        // la stessa registrazione attraverso più riascolti).
        if (recording && recordingMode !== 'post-playback' && typeof onStopRecording === 'function') {
            onStopRecording();
        }

        // Reset audio playback and state variables for a new play cycle
        audio.currentTime = 0; // Rewind audio to the beginning
        setAudioEnded(false); // Reset audio ended state
        audio.play(); // Start audio playback
        setHasPlayed(true); // Indicate that audio has been played at least once

        if (typeof onPlay === 'function') {
            onPlay();
        }

        // Clear any previous graphical timeouts
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];

        // --- ACTUAL RECORDING START (Based on recordingMode) ---
        // If recordingMode is NOT 'post-playback', recording starts with audio playback.
        if (recordingMode !== 'post-playback' && typeof onStartRecording === 'function') {
            onStartRecording();
        }

        // --- SYLLABLES-SPECIFIC GRAPHICAL TOGGLING ---
        // Apply graphical mic toggling ONLY for 'syllables' exercise in 'playback' mode
        if (exerciseId === 'syllables' && recordingMode === 'playback') {
            setSyllablesToggleMicActive(false); // Start graphical mic off for Syllables toggling
            const toggleTimes = toggleSchedule[context] || toggleSchedule.default;

            toggleTimes.forEach((time) => {
                const timeout = setTimeout(() => {
                    setSyllablesToggleMicActive(prev => !prev);
                }, time);
                timeoutsRef.current.push(timeout);
            });
        }

        // --- AUDIO END EVENT LISTENER ---
        // Define a function to handle what happens when the audio playback ends
        const handleAudioEnd = () => {
            setAudioEnded(true); // Update state to reflect audio has ended

            // --- ACTUAL RECORDING START/STOP (Based on recordingMode) ---
            if (recordingMode === 'post-playback' && typeof onStartRecording === 'function') {
                // In 'post-playback' mode, recording starts when audio ends
                onStartRecording();
            } else if (recordingMode !== 'post-playback' && typeof onStopRecording === 'function') {
                // In 'playback' mode (default), recording stops when audio ends
                onStopRecording();
            }

            // For Syllables, ensure the graphical mic turns off at audio end (after any toggles)
            // Clear any pending graphical timeouts as audio has ended
            timeoutsRef.current.forEach(clearTimeout);
            timeoutsRef.current = [];
            if (exerciseId === 'syllables' && recordingMode === 'playback') {
                setSyllablesToggleMicActive(false);
            }

            // Important: Remove the event listener after it has fired once
            audio.removeEventListener('ended', handleAudioEnd);
            endedHandlerRef.current = null;
        };

        // Attach the 'ended' event listener to the audio element.
        // { once: true } ensures it only fires a single time per playback.
        endedHandlerRef.current = handleAudioEnd;
        audio.addEventListener('ended', handleAudioEnd, { once: true });
    };

    /**
     * Handles the validate button click event.
     * Prevents validation if audio hasn't finished playing.
     * Ensures recording is stopped before proceeding with validation.
     */
    const handleValidate = () => {
        // Show an alert if the user tries to validate before the audio finishes
        if (!audioEnded && hasPlayed) {
            // Using a simple alert for immediate feedback, as per project guidelines (though a custom modal is preferred in real apps)
            alert('Please wait for the audio to finish before proceeding to the next exercise.');
            return;
        }

        // --- RECORDING BEHAVIOR STOP (ALL MODES) ---
        // Always attempt to stop recording on validate if it's active.
        // This is crucial for 'post-playback' mode, but also a safety net for 'playback' mode.
        if (recording && typeof onStopRecording === 'function') {
            onStopRecording();
        }

        // Proceed with the validation logic
        onValidate();
    };

    // Determine the active state for the microphone button's visual
    let micActiveForButton = false;
    if (exerciseId === 'syllables' && recordingMode === 'playback') {
        // For Syllables in playback mode, use the specific graphical toggle state
        micActiveForButton = syllablesToggleMicActive;
    } else {
        // For all other exercises/modes, the graphical mic active state
        // directly reflects the actual recording state (from useRecorder hook)
        micActiveForButton = recording;
    }

    // Determine if the validate button should be enabled
    // It's enabled if the audio hasn't been played yet, or if it has played and ended.
    const canValidate = !hasPlayed || audioEnded;

    return (
        <div style={styles.container}>
            {/* Audio element to play the exercise sound */}
            <audio ref={audioRef} src={audioSrc} preload="auto" />

            {/* Play/Repeat button */}
            <button
                onClick={handlePlay}
                style={{ ...styles.button, backgroundColor: colors.repeat }}
                aria-label={hasPlayed ? "Repeat Audio" : "Play Audio"}
            >
                <img
                    src={
                        hasPlayed
                            ? '/assets/icons/repeat1.png' // Icon for repeating
                            : '/assets/icons/play4.png' // Icon for initial play
                    }
                    alt={hasPlayed ? "Repeat" : "Play"}
                    style={styles.icon}
                />
            </button>

            {/* Microphone button (visual indicator only, not interactive) */}
            <button
                onClick={() => {}} // No action on click, as it's just an indicator
                style={{
                    ...styles.button,
                    backgroundColor: micActiveForButton ? '#ffcccc' : colors.record, // Changes color based on conditional micActiveForButton
                    cursor: 'default' // No pointer cursor as it's not interactive
                }}
                disabled // The button is always disabled as it's purely visual
                aria-label="Microphone Status"
            >
                <img
                    src="/assets/icons/microphone4.png"
                    alt="Record"
                    style={styles.icon}
                />
            </button>

            {/* Validate button */}
            <button
                onClick={handleValidate}
                style={{
                    ...styles.button,
                    backgroundColor: canValidate ? colors.check : '#cccccc', // Color changes based on validation readiness
                    cursor: canValidate ? 'pointer' : 'not-allowed', // Cursor changes based on readiness
                    opacity: canValidate ? 1 : 0.6 // Opacity changes based on readiness
                }}
                disabled={!canValidate} // Button is disabled if not ready for validation
                aria-label="Validate Answer"
            >
                <img
                    src="/assets/icons/validate1.png"
                    alt="Validate"
                    style={styles.icon}
                />
            </button>
        </div>
    );
}

// Styles for the component
const styles = {
    container: {
        position: 'absolute',
        top: '80%',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#eaeaf6',
        padding: '20px 30px',
        borderRadius: '30px',
        display: 'flex',
        gap: '40px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        zIndex: 3,
        fontFamily: "'Autour One', cursive", // Example font
    },
    button: {
        width: '90px',
        height: '90px',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        transition: 'background-color 0.3s ease, opacity 0.3s ease', // Smooth transitions
    },
    icon: {
        width: '60px',
        height: '60px',
        objectFit: 'contain',
    },
};

